import type {
  DeviceResourceDiagnosticsReadApi,
  SearchDeviceCallsV2MutationRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import type { SearchOrganizationsV1QueryParamsApi } from '@ingenico/api-sdk-provisioning-public';
import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { useBusinessServiceRequest, useQueryFirstLoading } from '@ingenico/react-section-utils';
import { DATAGRID_DEFAULT_PAGE_SIZE } from '@ingenico/react-ui/datagrid';
import type { OnTriggerProps, UseDatagridQueryTriggerProps } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import { useDatagridQueryTrigger } from '@ingenico/react-ui/hooks/use-datagrid-query-trigger';
import type { EmptyListProps } from '@ingenico/react-ui/notice-card';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import type { GridRowParams, GridSortModel } from '@mui/x-data-grid-pro';
import { toString } from 'lodash-es';
import type { MutableRefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { DeviceManagementService } from '../../domain/business/services/deviceManagement.service';
import { useLogicalDeviceDiagnosticsListColumns } from '../../hooks/useLogicalDeviceDiagnosticsListColumns.hook';
import type { UseDeviceDiagnosticsListContent } from './DeviceDiagnosticsListContent.model';
import {
  defaultDeviceDiagnosticsListContentSort,
  getDeviceDiagnosticsListContentErrorMsg,
  transformSortModelForRequest,
} from './DeviceDiagnosticsListContent.util';
import { useGridPropsSetup } from './hooks/useGriPropsSetup.hook';
import { useInitialSetup } from './hooks/useInitialSetup.hook';

export function useDeviceDiagnosticsListContent(): UseDeviceDiagnosticsListContent {
  const { t, datagridRef, navigate, isTranslationReady } = useInitialSetup();

  const [offset, setOffset] = useState(0);

  const [limit, setLimit] = useState(DATAGRID_DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<GridSortModel>(defaultDeviceDiagnosticsListContentSort);

  //When accessing from the terminal page, the params is called as terminalId
  const { logicalDeviceId, terminalId } = useParams<{
    logicalDeviceId: string;
    terminalId: string;
  }>();

  const location = useLocation();

  const { columns } = useLogicalDeviceDiagnosticsListColumns();

  const body = useMemo<SearchDeviceCallsV2MutationRequestApi>(
    () => ({
      filters: [],
      sort: transformSortModelForRequest(sort),
    }),
    [sort],
  );

  const params = useMemo<SearchOrganizationsV1QueryParamsApi>(() => {
    return { offset, limit };
  }, [offset, limit]);

  const query = useMemo(
    () => ({ body, params, deviceId: logicalDeviceId ? toString(logicalDeviceId) : toString(terminalId) }),
    [body, logicalDeviceId, params, terminalId],
  );

  const deviceDiagnosticsLogsQuery = useBusinessServiceRequest(
    DeviceManagementService,
    useCallback((deviceManagementService) => deviceManagementService.getSearchDeviceDiagnostics(query), [query]),
  );

  const total = useMemo<number>(() => {
    return deviceDiagnosticsLogsQuery.data?.pagination?.totalResults ?? 0;
  }, [deviceDiagnosticsLogsQuery.data?.pagination?.totalResults]);

  const onTrigger = useCallback(({ page, pageSize, sort: newSort }: OnTriggerProps) => {
    setOffset(page * pageSize);
    setLimit(pageSize);
    setSort(newSort);
  }, []);

  const serverData = useMemo<UseDatagridQueryTriggerProps['serverData']>(() => {
    return {
      offset,
      limit,
      total,
    };
  }, [limit, offset, total]);

  const datagridTriggers = useDatagridQueryTrigger({
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    onTrigger,
    serverData,
  });

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<DeviceResourceDiagnosticsReadApi>) => {
      const { id } = row;
      const path = location.pathname.includes('diagnostics') ? toString(id) : `diagnostics/${id}`;
      void navigate(path);
    },
    [navigate, location],
  );

  const getRowId = useCallback((element: DeviceResourceDiagnosticsReadApi) => toString(element.id), []);

  const isLoading = useMemo(() => deviceDiagnosticsLogsQuery.isLoading, [deviceDiagnosticsLogsQuery.isLoading]);

  const isFirstLoading = useQueryFirstLoading(deviceDiagnosticsLogsQuery) && !deviceDiagnosticsLogsQuery?.isError;

  const shouldDisplayGrid = useMemo(
    () => (deviceDiagnosticsLogsQuery.isSuccess || isLoading) && isTranslationReady,
    [deviceDiagnosticsLogsQuery.isSuccess, isLoading, isTranslationReady],
  );

  const gridProps = useGridPropsSetup({
    handleRowClick,
    datagridRef: datagridRef as MutableRefObject<GridApiPremium>,
    total,
    getRowId,
    isLoading,
    shouldDisplayGrid,
    onPaginationModelChange: datagridTriggers.onPaginationModelChange,
    onSortModelChange: datagridTriggers.onSortModelChange,
  });

  const filteredDeviceDiagnosticsLogsData = useMemo(
    () => deviceDiagnosticsLogsQuery?.data?.data ?? [],
    [deviceDiagnosticsLogsQuery?.data?.data],
  );

  const errorMessage = useMemo(
    () => getDeviceDiagnosticsListContentErrorMsg(t, deviceDiagnosticsLogsQuery?.error),
    [t, deviceDiagnosticsLogsQuery?.error],
  );
  const errorStatus = useMemo(() => deviceDiagnosticsLogsQuery?.error?.status, [deviceDiagnosticsLogsQuery?.error]);
  const errorTitle = useMemo(() => t('title.noDiagnosticsFound', { ns: LNGS_NAMESPACE_LOGICAL_DEVICE.DEFAULT }), [t]);

  const noRowsProps = useMemo<EmptyListProps>(
    () => ({
      image: 'empty-services',
      messageLabel: 'emptyDeviceDiagnosticsLogs.message',
      namespace: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE,
      titleLabel: '',
      isBordered: false,
    }),
    [],
  );

  return {
    columns,
    filteredDeviceDiagnosticsLogsData,
    gridProps,
    deviceDiagnosticsLogsQuery,
    isFirstLoading,
    errorMessage,
    total,
    noRowsProps,
    errorStatus,
    errorTitle,
    ...datagridTriggers,
  };
}
